var _ = require('underscore');
var ko = require('knockout');

var Cookie = require('../util/cookie.js');
var pushState = require('../tk/push_state.js');
var viewModelBase = require('../tk/view_model/base.js');

// A Filter view model manages the UI that determines whether a set of opaque
// ids are within the current set of families or not. The filter is responsible
// for fetching its own ids from the api if it doesn't have them yet.
//
// apiFonts  - Fonts used to fetch opaque ids.
// options: - Object with options for the filter
//   namespace - String bit namespace.
//   value     - String bit value.
//   fvd       - String or null, the preferred FVD for when
//               this filter is enabled.
//   label     - String text name of the filter.
//   scripts   - The specific scripts that this filter is targeted for.
//
var Filter = viewModelBase.create({
  init: function(apiFonts, options) {
    this._apiFonts = apiFonts;

    this.fvd = options.fvd;
    this.isCheckbox = ko.observable(false);
    this.label = options.label;
    this.namespace = options.namespace;
    this.preventDeselect = options.preventDeselect;
    this.scripts = options.scripts;
    this.value = options.value;

    // Store this as a property on the object to make it easier to replace with
    // a mock version during testing.
    this._pushState = pushState;

    // StateHandler instance for generating urls, if any.
    this._stateHandler = ko.observable();

    // The set of opaque ids describing this filter. Should be null if no data has
    // been loaded.
    this.opaqueIds = null;

    // True if the filter currently enabled.
    this._enabled = ko.observable(false);
    this.isEnabled = ko.computed({
      read: this._enabled,
      write: this._ensureLoadedAndSetEnabled,
      owner: this
    });

    // True if we are waiting for a response for the opaque ids. Can't be set
    // externally.
    this._loading = ko.observable(false);
    this.isLoading = ko.computed({ read: this._loading });

    // True if the filter currently focused.
    this.isFocused = ko.observable(false);

    // The value if enabled or null otherwise. (Necessary for form version radio
    // buttons).
    this.isEnabledValue = ko.computed(this._isEnabledValue_read, this);
  },

  // Public: Return a unique id for this filter.
  //
  // Returns String.
  getId: function() {
    var cssID = [
      'filter-option',
      this.namespace,
      this.value
    ].join('-');

    // If this filter supports more than one script, find the first non-latin
    // one and append it to the id as a postfix.
    if (this._hasNonLatinScripts()) {
      return [cssID, this._getFirstNonLatinScript()].join('-');
    }

    return cssID;
  },

  // Public: Set the state handler object to use to generate urls.
  //
  // stateHandler - StateHandler instance to generate urls with.
  //
  // Returns nothing.
  useStateHandler: function(stateHandler) {
    this._stateHandler(stateHandler);
  },

  // Public: Set the opaque ids directly. Doing this causes the
  // filter to be considered 'loaded'.
  //
  // Returns nothing.
  setOpaqueIds: function(ids) {
    this.opaqueIds = ids;
  },

  // Public: Flip the isEnabled state of the filter.
  //
  // Returns nothing.
  flip: function() {
    this._ensureLoadedAndSetEnabled(!this._enabled());
  },

  // Public: Flips the isEnabled state of the filter if pushState is supported.
  // Otherwise, returns true so that any binding's default handler can take over.
  //
  // Returns Boolean true if pushState isn't enabled, nothing otherwise.
  flipIfPushState: function() {
    if (this._pushState.pushStateIsSupported()) {
      this.flip();
    } else {
      // Allow default click behavior.
      return true;
    }
  },

  // Public: Handles clicking on the parent element for the filter.
  //
  // Returns Nothing.
  handleClick: function(viewModel, event) {
    var asForm = event.currentTarget.getAttribute('data-as-form') == 'true' ? true : false;
    if (asForm) {
      return this.flip();
    }

    // If the filter is already enabled and preventDeselect is true, then behave
    // like a traditional radio button and don't allow the button to deselected
    // once it's selected.
    if (this.isEnabled() && this.preventDeselect) {
      return;
    }

    this.flipIfPushState();

    // For now, treat the script filters specially and store the selected value
    // as a cookie.
    if (this.namespace == 'script') {
      this._storeScriptSelectionAsCookie();
    }
  },

  // Private: Sets the value of enabled to the given value after ensuring that the
  // opaque IDs for this filter have been loaded from the API. If the IDs are
  // already loaded or we're setting to false, it sets the value immediately. If
  // the IDs are not yet loaded and we're setting the value to true, it kicks off
  // a request and sets enabled to true when the request completes successfully.
  // If the IDs are currently loading, it ignores the new value.
  //
  // newEnabledValue - A boolean value to set enabled to.
  //
  // Returns nothing.
  _ensureLoadedAndSetEnabled: function(newEnabledValue) {
    if (!newEnabledValue || !_.isNull(this.opaqueIds)) {
      this._enabled(newEnabledValue);
    } else {
      if (!this._loading()) {
        this._loading(true);
        this._apiFonts.getFilteredOpaqueIds(this.namespace, this.value).
          done(function(opaqueIds) {
            this.setOpaqueIds(opaqueIds);
            this._loading(false);
            this._enabled(newEnabledValue);
          }.bind(this)).error(function(status, error) {
            this._loading(false);
          }.bind(this));
      }
    }
  },

  // Read isEnabledValue.
  //
  // Returns the value of this filter if isEnabled is true, or null otherwise.
  _isEnabledValue_read: function() {
    return this._enabled() ? this.value : null;
  },

  // Public: Does this filter support non-latin scripts?
  //
  // Returns Boolean.
  _hasNonLatinScripts: function() {
    if (!this.scripts) {
      return false;
    }

    return !!this._getFirstNonLatinScript();
  },

  // Public: Get the first script supported by the filter that isn't latin.
  //
  // Returns string
  _getFirstNonLatinScript: function() {
    return _.find(this.scripts, function(script) {
      return script != 'latin';
    });
  },

  // Generate a URL to flip the state of this filter.
  //
  // If the browser supports push state, or no state is provided, the url is just
  // a representative hash like #filter-namespace-value.
  //
  // If the browser doesn't support push state and a state is provided, the url
  // is determined by the state so that the link functions when the user clicks
  // it.
  //
  // Returns the url to use in a link.
  url: function() {
    var stateHandler = this._stateHandler();
    if (!this._pushState.pushStateIsSupported() && stateHandler) {
      var state = stateHandler.snapshot();
      var isCheckbox = this.isCheckbox();

      // First, we need to remove this namespace and value from the existing
      // state, if present. If the filter is a radio style (not a checkbox), we
      // remove all filters in the same namespace.
      state.filters = _.reject(state.filters, function(filter) {
        return filter.ns == this.namespace && (filter.v == this.value || !isCheckbox);
      }, this);

      // If the filter is currently disabled, we want to enable it by adding it
      // to the current state.
      if (!this._enabled()) {
        state.filters.push({ ns: this.namespace, v: this.value });
      }

      return stateHandler.stateToUrl(state);
    } else {
      return '#filter-' + this.namespace + '-' + this.value;
    }
  },

  // Internal: Stores this filter's value as a cookie after the user has
  // selected it.
  //
  // This is not the ideal place to do this, but it's the easiest way to
  // integrate with the existing filtering code. This should be rethought in the future.
  //
  // Returns Nothing.
  _storeScriptSelectionAsCookie: function() {
    Cookie(
      Filter.SELECTED_SCRIPT_COOKIE_NAME,
      this.value,
      { expires: 365 });
  }
});

Filter.ID_PREFIX = 'filter-option';
Filter.SELECTED_SCRIPT_COOKIE_NAME = 'tk_selected_script_filter';

module.exports = Filter;
